export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// `en-CA` formats as YYYY-MM-DD, which is the format the GraphQL date inputs
// take, so the calendar date can be read straight out of the formatter.
const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

const dateFormatterFor = (timezone: string): Intl.DateTimeFormat => {
    const cached = dateFormatterCache.get(timezone);
    if (cached) {
        return cached;
    }

    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    dateFormatterCache.set(timezone, formatter);
    return formatter;
};

/**
 * The calendar date of an instant, in the given zone, as YYYY-MM-DD.
 *
 * The zone is explicit because the same instant is a different calendar date
 * either side of local midnight, and a UTC container would otherwise resolve
 * "today" four to five hours ahead of an Eastern viewer for part of every day.
 */
export const date = (instant: Date, timezone: string): string =>
    dateFormatterFor(timezone).format(instant);

/** An instant as a full ISO-8601 timestamp. Zone-independent. */
export const datetime = (instant: Date): string => instant.toISOString();

/** An instant shifted by a whole number of days. */
export const addDays = (instant: Date, days: number): Date =>
    new Date(instant.getTime() + days * ONE_DAY_MS);

/** The calendar date `days` from an instant, in the given zone. */
export const dateInDays = (instant: Date, timezone: string, days: number): string =>
    date(addDays(instant, days), timezone);

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

const partsFormatterFor = (timezone: string): Intl.DateTimeFormat => {
    const cached = partsFormatterCache.get(timezone);
    if (cached) {
        return cached;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    partsFormatterCache.set(timezone, formatter);
    return formatter;
};

/**
 * The zone's offset from UTC at an instant, in milliseconds.
 *
 * Read by formatting the instant in the zone and reinterpreting those wall-clock
 * fields as if they were UTC. The difference is the offset, which is what makes
 * the result correct either side of a DST transition rather than assuming a
 * fixed offset per zone.
 */
const zoneOffsetMs = (instant: Date, timezone: string): number => {
    const parts = partsFormatterFor(timezone).formatToParts(instant);
    const field = (type: Intl.DateTimeFormatPartTypes): number =>
        Number(parts.find((part) => part.type === type)?.value ?? '0');

    // `hour12: false` yields hour 24 for midnight in some runtimes.
    const hour = field('hour') % 24;
    const asIfUtc = Date.UTC(
        field('year'),
        field('month') - 1,
        field('day'),
        hour,
        field('minute'),
        field('second'),
    );
    return asIfUtc - instant.getTime();
};

/** A YYYY-MM-DD calendar date shifted by a whole number of days. */
export const addCalendarDays = (calendarDate: string, days: number): string => {
    const [year, month, day] = calendarDate.split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day) + days * ONE_DAY_MS);
    return shifted.toISOString().slice(0, 10);
};

/**
 * The instant midnight begins on a calendar date in a zone, as an ISO timestamp.
 *
 * The offset is resolved twice because the offset that applies at midnight is not
 * necessarily the one that applies at the instant used to look it up: on a DST
 * boundary the first guess lands on the wrong side. The second pass re-reads the
 * offset at the guessed instant and corrects it.
 */
export const startOfDayInZone = (calendarDate: string, timezone: string): string => {
    const [year, month, day] = calendarDate.split('-').map(Number);
    const asIfUtc = Date.UTC(year, month - 1, day);

    const firstGuess = new Date(asIfUtc - zoneOffsetMs(new Date(asIfUtc), timezone));
    const corrected = new Date(asIfUtc - zoneOffsetMs(firstGuess, timezone));
    return corrected.toISOString();
};

/**
 * The last instant of a calendar date in a zone, as an ISO timestamp.
 *
 * A millisecond before the next day opens, so an inclusive `lte` on a timestamp
 * covers the whole day. A range that ended at midnight would drop everything after
 * it, which is most of the day.
 */
export const endOfDayInZone = (calendarDate: string, timezone: string): string => {
    const nextMidnight = Date.parse(startOfDayInZone(addCalendarDays(calendarDate, 1), timezone));
    return new Date(nextMidnight - 1).toISOString();
};

/**
 * The calendar date of the Sunday that opens the week containing an instant.
 *
 * Pinnacle reports on the Medicare week, which runs Sunday to Saturday. The
 * weekday is taken in the org's zone, so a run just after midnight UTC does not
 * roll into the next week for an Eastern org.
 */
export const startOfMedicareWeek = (instant: Date, timezone: string): string => {
    const today = date(instant, timezone);
    const [year, month, day] = today.split('-').map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return addCalendarDays(today, -weekday);
};

export const GraphInputHelpers = {
    date,
    datetime,
    addDays,
    dateInDays,
    addCalendarDays,
    startOfDayInZone,
    endOfDayInZone,
    startOfMedicareWeek,
};
